import React from "react";
import {
    TextField,
    MenuItem,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    FormGroup,
    FormHelperText,
    InputLabel,
    Select,
    OutlinedInput,
    InputAdornment,
    IconButton,
    Chip,
    Box,
} from "@mui/material";
import { DatePicker, DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { Clear } from "@mui/icons-material";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

type FieldVariant = "input" | "outlined" | "select" | "radio" | "date" | "checkbox" | "datetime" | "time";

export interface Option {
    value: string | number;
    label: string | number;
    disabled?: boolean;
}

interface FieldControlProps {
    keyName: string;
    label: string;
    variant: FieldVariant;
    value?: any;
    onChange: (key: string, value: any) => void;
    options?: Option[];
    multiline?: boolean;
    rows?: number;
    error?: boolean;
    helperText?: string | null;
    checked?: boolean;
    required?: boolean;
    minDate?: any;
    maxDate?: any;
    loading?: boolean;
    multiSelect?: boolean;
    disabled?: boolean;
}

export const FieldControl: React.FC<FieldControlProps> = ({
    keyName,
    label,
    variant,
    value,
    onChange,
    options = [],
    multiline,
    rows,
    error,
    helperText,
    checked,
    required = false,
    minDate,
    maxDate,
    loading,
    multiSelect,
    disabled
}) => {
    switch (variant) {
        case "input":
        case "outlined":
            return (
                <TextField
                    disabled={disabled}
                    fullWidth
                    variant={variant === "outlined" ? "outlined" : "outlined"}
                    label={label}
                    value={value ?? ""}
                    onChange={(e) => onChange(keyName, e.target.value)}
                    multiline={multiline}
                    rows={rows}
                    error={error}
                    helperText={helperText}
                    required={required}
                />
            );
        case "select":
            return (
                <FormControl
                    fullWidth
                    error={error}
                    disabled={disabled}
                    required={required}
                >
                    <InputLabel>{label}</InputLabel>
                    <Select
                        label={label}
                        multiple={multiSelect}
                        value={value || (multiSelect ? [] : '')}
                        onChange={(e) => onChange(keyName, e.target.value)}
                        input={
                            <OutlinedInput
                                label={label}
                                endAdornment={
                                    value &&
                                    ((Array.isArray(value) && value?.length > 0) ||
                                        (!Array.isArray(value) && value !== '')) && (
                                        <InputAdornment position="end">
                                            {!disabled || !required && (
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    sx={{ mr: 2 }}
                                                    disabled={disabled}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const emptyValue = Array.isArray(value) ? [] : '';
                                                        onChange(keyName, emptyValue);
                                                    }}
                                                >
                                                    <Clear />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    )
                                }
                            />
                        }
                        renderValue={(selected) => {
                            if (!Array.isArray(selected)) {
                                const label = options.find((opt) => opt.value === selected)?.label || '';
                                return label;
                            }
                            return (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const label = options.find((opt) => opt.value === value)?.label;
                                        return (
                                            <Chip
                                                key={value}
                                                label={label}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onDelete={() => {
                                                    const newValues = selected.filter((v) => v !== value);
                                                    onChange(keyName, newValues);
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            );
                        }}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 350,
                                    overflowY: 'auto',
                                },
                            },
                        }}
                    >
                        {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {multiSelect ? (
                                    <Checkbox
                                        checked={Array.isArray(value) && value.includes(option.value)}
                                        sx={{ p: 0, mr: 1, my: 0.3 }}
                                    />
                                ) : null}
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {(error || helperText) && (
                        <FormHelperText>{helperText}</FormHelperText>
                    )}
                </FormControl>
            );
        case "radio":
            return (
                <FormControl component="fieldset" required={required} error={error} fullWidth sx={{ px: 1 }}>
                    <FormLabel component="legend">{label}</FormLabel>
                    <RadioGroup row value={value ?? ""} onChange={(e) => onChange(keyName, e.target.value)}>
                        {options.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio color={error ? "error" : "primary"} />}
                                label={option.label}
                            />
                        ))}
                    </RadioGroup>
                    <FormHelperText>{helperText}</FormHelperText>
                </FormControl>
            );
        case "checkbox":
            return (
                <FormGroup
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                >
                    {options && options?.length > 0 ? (
                        options.map((option) => {
                            const isChecked = Array.isArray(value)
                                ? value.includes(option.value)
                                : !!value;
                            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                const checked = e.target.checked;
                                if (Array.isArray(value)) {
                                    onChange(
                                        keyName,
                                        checked
                                            ? [...value, option.value]
                                            : value.filter((v: any) => v !== option.value)
                                    );
                                } else {
                                    onChange(keyName, checked);
                                }
                            };

                            return (
                                <FormControlLabel
                                    key={option.value}
                                    required={required}
                                    control={<Checkbox checked={isChecked} onChange={handleChange} />}
                                    label={option.label}
                                />
                            );
                        })
                    ) : (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!value}
                                    onChange={(e) => onChange(keyName, e.target.checked)}
                                />
                            }
                            label={label}
                        />
                    )}
                </FormGroup>
            );
        case "datetime":
            return (
                <FormControl error={error} fullWidth required={required} disabled={disabled}>
                    <DateTimePicker
                        label={label}
                        format="DD/MM/YYYY HH:mm"
                        value={value ? dayjs.utc(value).local() : null}
                        onChange={(newVal) =>
                            onChange(
                                keyName,
                                newVal ? newVal.utc().toISOString() : null
                            )
                        }
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                error: error,
                                required: required,
                                helperText: helperText,
                            },
                        }}
                        minDate={minDate ? dayjs.utc(minDate).local() : undefined}
                        maxDate={maxDate ? dayjs.utc(maxDate).local() : undefined}
                    />
                </FormControl>
            );
        case "date":
            return (
                <FormControl error={error} fullWidth required={required} disabled={disabled}>
                    <DatePicker
                        label={label}
                        format="DD/MM/YYYY"
                        value={value ? dayjs.utc(value).local() : null}
                        onChange={(newVal) =>
                            onChange(
                                keyName,
                                newVal ? newVal.utc().startOf("day").toISOString() : null
                            )
                        }
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                error: error,
                                required: required,
                                helperText: helperText,
                            },
                        }}
                        minDate={minDate ? dayjs.utc(minDate).local() : undefined}
                        maxDate={maxDate ? dayjs.utc(maxDate).local() : undefined}
                    />
                </FormControl>
            );
        case "time":
            return (
                <FormControl error={error} fullWidth required={required} disabled={disabled}>
                    <TimePicker
                        disabled={disabled}
                        label={label}
                        value={value ? dayjs(value, "HH:mm:ss") : null}
                        onChange={(newVal) =>
                            onChange(
                                keyName,
                                newVal ? newVal.format("HH:mm:ss") : null
                            )
                        }
                        ampm={false}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                error: error,
                                required: required,
                                helperText: helperText,
                            },
                        }}
                    />
                </FormControl>
            );
        default:
            return null;
    }
};  